import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Mail } from 'lucide-react';
import { OPERATOR } from '../lib/operator';

export default function DeleteAccount() {
    const navigate = useNavigate();
    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="delete-account-page">
            <button onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">GrowDaily</p>
            <h1 className="font-serif text-4xl text-brand-ink mt-1 mb-3 inline-flex items-center gap-2"><Trash2 size={28} /> Delete your account &amp; data</h1>
            <p className="text-sm text-brand-ink2 leading-relaxed mb-8">
                This page explains how to delete your <strong>GrowDaily</strong> account and all associated
                data. GrowDaily is operated by {OPERATOR.controller} under the name {OPERATOR.brand}.
            </p>

            <div className="space-y-6">
                <section>
                    <h2 className="font-serif text-lg text-brand-ink mb-2">Option 1 — Delete in the app</h2>
                    <ol className="list-decimal list-inside text-sm text-brand-ink2 leading-relaxed space-y-1">
                        <li>Open the GrowDaily app and sign in.</li>
                        <li>Go to <strong>Profile</strong>.</li>
                        <li>Scroll to the bottom and tap <strong>“Delete account”</strong>.</li>
                        <li>Confirm to remove your account and the linked records handled by the account deletion function.</li>
                        <li>If a paid AI request is still running, check AI history and retry after it finishes or is refunded. Contact support if it stays pending; no account data is removed by that blocked attempt.</li>
                    </ol>
                </section>

                <section>
                    <h2 className="font-serif text-lg text-brand-ink mb-2 inline-flex items-center gap-2"><Mail size={16} /> Option 2 — Request by email</h2>
                    <p className="text-sm text-brand-ink2 leading-relaxed">
                        If you can’t access the app, email{' '}
                        <a href={`mailto:${OPERATOR.email}`} className="text-brand-green underline">{OPERATOR.email}</a>{' '}
                        from your registered address with the subject “Delete my account”. We verify and complete
                        your identity before handling the request. Do not send your password.
                    </p>
                </section>

                <section>
                    <h2 className="font-serif text-lg text-brand-ink mb-2">What gets deleted</h2>
                    <p className="text-sm text-brand-ink2 leading-relaxed">
                        The in-app function removes your account, plants, tasks, journal entries, harvest records,
                        sessions, AI history, bed layouts, consent records and push subscriptions linked to your user ID.
                        This is not a guarantee that every uploaded file, shared record or backup is erased by that button.
                        Contact us using the email above for a complete data-erasure request.
                    </p>
                </section>

                <section>
                    <h2 className="font-serif text-lg text-brand-ink mb-2">Data retention</h2>
                    <p className="text-sm text-brand-ink2 leading-relaxed">
                        Paid AI operation history is removed by account deletion. GrowDaily payment receipts,
                        balance and usage ledger records, backup copies and records held by payment providers
                        are separate from your active account and are not all erased by the account button.
                        We will explain any retention that applies when handling your request. Deleting your account
                        does not itself cancel a recurring payment agreement: cancel it with the payment provider first.
                    </p>
                </section>
            </div>
        </div>
    );
}
